import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  Upload, ArrowLeft, FileText, CheckCircle, AlertCircle, Download
} from 'lucide-react';
import { toast } from 'sonner';

const QBMBulkImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setImporting(true);
    const loadingToast = toast.loading('Importing questions...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/question-bank/bulk-import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.dismiss(loadingToast);
      setResults(response.data);
      
      if (response.data.success_count > 0) {
        toast.success(`Successfully imported ${response.data.success_count} questions`);
      }
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} questions had errors`);
      }
    } catch (err) {
      toast.dismiss(loadingToast);
      toast.error(err.response?.data?.detail || 'Failed to import questions');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = [
      'board_code',
      'subject_code',
      'paper_code',
      'topic_code',
      'subtopic_code',
      'question_number',
      'question_type',
      'question_text',
      'difficulty_level',
      'total_marks',
      'part_labels',
      'part_marks',
      'part_ao_types',
      'correct_answer',
      'options'
    ];

    const sampleData = [
      'IGCSE',
      'MATH',
      'P1',
      'T1',
      'ST1',
      '1',
      'structured',
      'Solve the following equation: 2x + 5 = 15',
      'medium',
      '4',
      'a|b',
      '2|2',
      'AO1|AO2',
      '',
      ''
    ];

    const csvContent = [headers.join(','), sampleData.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'question_bank_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <button 
            onClick={() => navigate('/qbm/dashboard')}
            className="flex items-center gap-2 text-purple-100 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Question Bank
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Upload className="w-8 h-8" />
                Bulk Import Questions
              </h1>
              <p className="mt-2 text-purple-200">Import questions from a CSV file</p>
            </div>
            <button
              onClick={downloadTemplate}
              className="flex items-center gap-2 px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg font-medium transition-all"
            >
              <Download className="w-5 h-5" />
              Download Template
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Upload Area */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-8">
          <div className="text-center">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
              id="csv-upload"
            />
            <label
              htmlFor="csv-upload"
              className={`block w-full p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                file 
                  ? 'border-green-300 bg-green-50' 
                  : 'border-slate-300 hover:border-purple-400 hover:bg-purple-50'
              }`}
            >
              {file ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="w-8 h-8 text-green-600" />
                  <div className="text-left">
                    <p className="font-medium text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                  <p className="text-slate-600">Click to select a CSV file</p>
                  <p className="text-sm text-slate-400 mt-2">
                    Download the template above to see the required format
                  </p>
                </>
              )}
            </label>
          </div>

          <button
            onClick={handleImport}
            disabled={!file || importing}
            className="w-full mt-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-medium rounded-lg transition-all"
          >
            {importing ? 'Importing...' : 'Import Questions'}
          </button>
        </div>

        {/* Results */}
        {results && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Import Results</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">Successful</span>
                </div>
                <p className="text-2xl font-bold text-green-600 mt-2">
                  {results.success_count}
                </p>
              </div>
              <div className="p-4 bg-red-50 rounded-lg">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Errors</span>
                </div>
                <p className="text-2xl font-bold text-red-600 mt-2">
                  {results.error_count}
                </p>
              </div>
            </div>

            {results.errors && results.errors.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Error Details:</h4>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {results.errors.map((error, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded-lg text-sm">
                      <span className="font-medium text-red-700">Row {error.row}:</span>{' '}
                      <span className="text-red-600">{error.error}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">CSV Format Instructions</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• <strong>board_code, subject_code, paper_code</strong> - Must match existing records</li>
            <li>• <strong>topic_code, subtopic_code</strong> - Optional, must match if provided</li>
            <li>• <strong>question_type</strong> - Either "mcq" or "structured"</li>
            <li>• <strong>difficulty_level</strong> - "easy", "medium", or "hard"</li>
            <li>• <strong>part_labels, part_marks, part_ao_types</strong> - Pipe-separated (e.g., "a|b|c")</li>
            <li>• <strong>options</strong> - For MCQ only, pipe-separated (e.g., "Option A|Option B|Option C|Option D")</li>
            <li>• <strong>correct_answer</strong> - For MCQ, the correct option text</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default QBMBulkImport;
